import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccessTimeIcon from "@mui/icons-material/AccessTime";


const backendUrl = "https://limitless-mesa-46798-263e04e95cf1.herokuapp.com";
// const backendUrl = "http://localhost:3000";

const formatElapsedTime = (seconds) => {
  const totalSeconds = Math.max(0, Number(seconds) || 0);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const Leaderboard = ({ onClose, onHeroChange, hasHero = false }) => {
  const [games, setGames] = useState({});
  const [loading, setLoading] = useState(true);

  const [gameNames, setGameNames] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [gameBackgrounds, setGameBackgrounds] = useState({});

  const userId = localStorage.getItem("userId");
  const username = localStorage.getItem("username");
  const userAvatar = localStorage.getItem("avatarUrl") || "/default-avatar.png";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Swipe
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const SWIPE_THRESHOLD = 55;
  const VERTICAL_GUARD = 45;

  const selectedGame = useMemo(() => {
    if (!gameNames.length) return null;
    return gameNames[Math.max(0, Math.min(selectedIndex, gameNames.length - 1))];
  }, [gameNames, selectedIndex]);

  const heroUrl = selectedGame ? gameBackgrounds[selectedGame] : null;

  // ✅ posílej aktuální heroUrl ven do App.js
  useEffect(() => {
    if (typeof onHeroChange === "function") {
      onHeroChange(heroUrl || null);
    }
  }, [heroUrl, onHeroChange]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await fetch(`${backendUrl}/leaderboard`);
        const data = await response.json();

        if (!Array.isArray(data)) {
          console.error("❌ Chyba: Backend nevrátil pole!", data);
          setGames({});
          setGameNames([]);
          setSelectedIndex(0);
          setGameBackgrounds({});
          return;
        }

        const groupedGames = data.reduce((acc, row) => {
          if (!acc[row.game_name]) acc[row.game_name] = [];
          acc[row.game_name].push(row);
          return acc;
        }, {});

        const names = Object.keys(groupedGames);

        const bgMap = {};
        data.forEach((row) => {
          if (!bgMap[row.game_name] && row.introscreenimage) {
            bgMap[row.game_name] = row.introscreenimage;
          }
        });

        setGames(groupedGames);
        setGameNames(names);
        setSelectedIndex(0);
        setGameBackgrounds(bgMap);
      } catch (error) {
        console.error("❌ Chyba při načítání leaderboardu:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, []);

  const goPrev = () => {
    if (!gameNames.length) return;
    setSelectedIndex((i) => (i - 1 + gameNames.length) % gameNames.length);
  };

  const goNext = () => {
    if (!gameNames.length) return;
    setSelectedIndex((i) => (i + 1) % gameNames.length);
  };

  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
    touchStartYRef.current = e.touches?.[0]?.clientY ?? null;
  };

  const handleTouchEnd = (e) => {
    const startX = touchStartXRef.current;
    const startY = touchStartYRef.current;
    const endX = e.changedTouches?.[0]?.clientX ?? null;
    const endY = e.changedTouches?.[0]?.clientY ?? null;

    touchStartXRef.current = null;
    touchStartYRef.current = null;

    if (startX == null || endX == null || startY == null || endY == null) return;

    const dx = endX - startX;
    const dy = endY - startY;

    if (Math.abs(dy) > VERTICAL_GUARD && Math.abs(dy) > Math.abs(dx)) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;

    if (dx < 0) goNext();
    if (dx > 0) goPrev();
  };

  const TopPlayerBadge = ({ rank, avatar, name, score, time }) => {
    const getRankColor = (r) => {
      if (r === 1) return "#FFD700";
      if (r === 2) return "#C0C0C0";
      if (r === 3) return "#CD7F32";
      return "#ffffff";
    };
    const rankColor = getRankColor(rank);
    const size = rank === 1 ? { xs: 120, sm: 140 } : { xs: 96, sm: 112 };

    return (
      <Box sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, width: { xs: 120, sm: 150 }, zIndex: 0 }}>
        <Box sx={{ position: "absolute", left: "50%", top: "40%", transform: "translateX(-50%)", width: "80%", height: "100%", background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.8) 50%, rgba(0,0,0,0) 100%)", zIndex: -1, pointerEvents: "none" }} />

        <Box sx={{ position: "relative", width: size, height: size }}>
{rank === 1 && (
  <Box
    sx={{
      position: "absolute",
      // Změna z -26px na -14px posune korunku níže.
      // Uprav si toto číslo podle tloušťky rámečku avataru, aby to sedělo přesně.
      top: "-20px",
      left: "50%",
      transform: "translateX(-50%)",
      width: "36px",
      height: "28px",
      zIndex: 10,
      pointerEvents: "none",
      filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.35))",

    }}
  >
  <svg viewBox="0 0 64 48" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
  <defs>
    {/* Fade jen nahoře (bílé špičky → transparentní dolů) */}
    <linearGradient id="tipFade" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
      <stop offset="35%" stopColor="rgba(255,255,255,0.35)" />
      <stop offset="60%" stopColor="rgba(255,255,255,0)" />
    </linearGradient>
    <linearGradient id="crownSweep" x1="0" y1="0" x2="1" y2="0">
  <stop offset="0%" stopColor="rgba(255,255,255,0)" />
  <stop offset="45%" stopColor="rgba(255,255,255,0)" />
  <stop offset="50%" stopColor="rgba(255,255,255,0.55)" />
  <stop offset="55%" stopColor="rgba(255,255,255,0)" />
  <stop offset="100%" stopColor="rgba(255,255,255,0)" />
</linearGradient>

<clipPath id="crownClip">
  <path
    d="
      M 10 38
      L 12 10
      L 25 24
      L 32 2
      L 39 24
      L 52 10
      L 54 38
      Q 32 32 10 38
      Z
    "
  />
</clipPath>

  </defs>

  {/* Základ korunky (flat žlutá, jako máš teď) */}
  <path
    fill="#FFD84D"
    d="
      M 10 38
      L 12 10
      L 25 24
      L 32 2
      L 39 24
      L 52 10
      L 54 38
      Q 32 32 10 38
      Z
    "
  />

  {/* ✅ Overlay stejným tvarem – gradient z bílé nahoře do průhledna dolů */}
  <path
    fill="url(#tipFade)"
    d="
      M 10 38
      L 12 10
      L 25 24
      L 32 2
      L 39 24
      L 52 10
      L 54 38
      Q 32 32 10 38
      Z
    "
  />
  <g clipPath="url(#crownClip)">
  <rect
    x="-40"
    y="0"
    width="40"
    height="48"
    fill="url(#crownSweep)"
    opacity="0.9"
  >
    <animate
      attributeName="x"
      from="-40"
      to="80"
      dur="2.6s"
      repeatCount="indefinite"
    />
  </rect>
</g>

</svg>


  </Box>
)}





 <Box
  sx={{
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    border: `1.5px solid ${rankColor}`,
    padding: 0,
    boxShadow: `0 0 10px ${rankColor}AA`, // klidně nech jak máš
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "visible",

    ...(rank === 1 && {
      "&::after": {
  content: '""',
  position: "absolute",
  inset: "-3px",
  borderRadius: "50%",
  background:
    "conic-gradient(from 0deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0) 300deg, rgba(255,255,255,0.65) 330deg, rgba(255,255,255,0) 360deg)",
  filter: "blur(0.6px)",
  opacity: 0.75,
  animation: "orbitGlow 3.2s linear infinite",
  pointerEvents: "none",

  // ✅ UDĚLÁ Z TOHO JEN TENKÝ OKRAJ (ring), aby to nezasahovalo do fotky:
  WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 2px))",
  mask: "radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 2px))",
},

      "@keyframes orbitGlow": {
        "0%": { transform: "rotate(0deg)" },
        "100%": { transform: "rotate(360deg)" },
      },
    }),
  }}
>
  <Avatar src={avatar || "/default-avatar.png"} sx={{ width: "100%", height: "100%", borderRadius: "50%" }} />
</Box>

        </Box>

        <Typography sx={{ color: "#F2EDE4", textAlign: "center", fontFamily: '"Cactus Classical Serif", serif', fontSize: "13px", fontWeight: 400, lineHeight: "16px", textTransform: "uppercase", marginTop: "10px", marginBottom: "4px", maxWidth: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
          {name}
        </Typography>

        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
          <span style={{ color: "#FFD24A", fontSize: "15px", transform: "translateY(-1px)"  }}>{"\u2605"}</span>
          <Typography sx={{ color: "rgba(242, 237, 228, 0.40)", textAlign: "center", fontFamily: '"Cactus Classical Serif", serif', fontSize: "15px", fontWeight: 400, lineHeight: "16px" }}>
            {score}
          </Typography>
        </Box>

        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
         <AccessTimeIcon
  sx={{
    fontSize: 20,
    color: "rgba(255,255,255,0.6)",
  }}
/>

          <Typography sx={{ color: "rgba(242, 237, 228, 0.40)", textAlign: "center", fontFamily: '"Cactus Classical Serif", serif', fontSize: "14px", fontWeight: 400, lineHeight: "16px" }}>
            {time}
          </Typography>
        </Box>
      </Box>
    );
  };

  const PlayerRow = ({ rank, avatar, name, score, time, isCurrentUser = false }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        paddingX: { xs: 1.25, sm: 2 },
        paddingY: { xs: 0.9, sm: 1.2 },
        backgroundColor: "rgba(20, 22, 26, 0.8)",
        borderRadius: "12px",
        marginBottom: "10px",
        border: isCurrentUser ? "1px solid rgba(76, 175, 80, 0.45)" : "1px solid rgba(255, 255, 255, 0.08)",
      }}
    >
      <Typography sx={{ fontSize: { xs: "0.86rem", sm: "0.96rem" }, fontWeight: 770, color: "#ffffff", width: "22px", marginRight: "12px", fontFamily: "'Cactus Classical Serif', serif" }}>
        {rank}.
      </Typography>

      <Box sx={{ position: "relative", width: { xs: 34, sm: 38 }, height: { xs: 34, sm: 38 }, marginRight: "12px", flexShrink: 0 }}>
        <Avatar src={avatar || "/default-avatar.png"} sx={{ width: "100%", height: "100%", borderRadius: "50%", border: "1.5px solid rgba(255, 255, 255, 0.2)" }} />
      </Box>

      <Typography sx={{ fontWeight: 600, color: isCurrentUser ? "#dff7df" : "#f2ede4", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: { xs: "0.86rem", sm: "0.96rem" }, flex: 1, minWidth: 0, fontFamily: "'Cactus Classical Serif', serif" }}>
        {name}
      </Typography>
{/* ✅ Pravý blok (score + time vedle sebe), vždy zarovnaný doprava */}
<Box
  sx={{
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",        // mezera mezi score a časem
    marginRight: "16px",
    flexShrink: 0,      // ať se ten blok nesmršťuje
  }}
>
  {/* SCORE */}
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
    }}
  >
    <span
      style={{
        fontSize: "18px",
        color: "#FFD24A",
        display: "inline-block",
        transform: "translateY(-1px)",
        lineHeight: 1,
      }}
    >
      {"\u2605"}
    </span>

    <Typography
      sx={{
        color: "#f2ede4",
        fontWeight: 600,
        fontSize: "16px",
        fontFamily: "'Cactus Classical Serif', serif",
        lineHeight: 1,
      }}
    >
      {score}
    </Typography>
  </Box>

  {/* ČAS */}
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      minWidth: "110px",        // ✅ drží šířku času stejnou pro všechny řádky
      justifyContent: "flex-end",
    }}
  >
 <AccessTimeIcon
  sx={{
    fontSize: 20,
    color: "rgba(255,255,255,0.6)",
  }}
/>

    <Typography
      sx={{
        fontSize: "16px",
        color: "rgba(242, 237, 228, 0.7)",
        fontFamily: "'Cactus Classical Serif', serif",
        lineHeight: 1,
      }}
    >
      {time}
    </Typography>
  </Box>
</Box>

    </Box>
  );

  return (
    <Box
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      sx={{
        position: "relative",
        overflow: "hidden",
        background: "transparent",
        boxShadow: "none",
      }}
    >
      {/* CONTENT */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          pt: hasHero ? { xs: 22, sm: 26 } : { xs: 2, sm: 2 },
          px: { xs: 0, sm: 0 }, // padding řeší App.js wrapper
         pb: {
  xs: "calc(64px + 16px + env(safe-area-inset-bottom))",
  sm: "calc(72px + 16px + env(safe-area-inset-bottom))",
}, // místo pro spodní přepínač
        }}
      >
        {loading ? (
          <CircularProgress sx={{ color: "#fff", display: "block", margin: "auto" }} />
        ) : Object.keys(games).length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            Žádné dokončené hry.
          </Typography>
        ) : (
          <>
            {selectedGame && games[selectedGame] && (() => {
              const gameResults = games[selectedGame];
              const topThree = gameResults.slice(0, 3);
              const rankFourPlus = gameResults.slice(3, 5);

              let userResult = null;
              let userRank = -1;
              if (userId) {
                const userIndex = gameResults.findIndex((g) => g.user_id && g.user_id.toString() === userId);
                if (userIndex !== -1) {
                  userResult = gameResults[userIndex];
                  userRank = userIndex + 1;
                }
              }
              const isUserInTopFive = userRank > 0 && userRank <= 5;

              return (
                <Box>
                  {topThree.length > 0 && (
                    <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: { xs: "4px", sm: "8px" }, marginTop: { xs: "48px", sm: "64px" },
marginBottom: "100px", position: "relative", paddingBottom: { xs: "16px", sm: "20px" } }}>
                      {topThree[1] && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", order: 1, marginBottom: "-20px" }}>
                          <TopPlayerBadge rank={2} avatar={topThree[1].avatar_url} name={topThree[1].username} score={topThree[1].rating} time={formatElapsedTime(topThree[1].elapsed_time)} />
                        </Box>
                      )}

                      {topThree[0] && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", order: 2 }}>
                          <TopPlayerBadge rank={1} avatar={topThree[0].avatar_url} name={topThree[0].username} score={topThree[0].rating} time={formatElapsedTime(topThree[0].elapsed_time)} />
                        </Box>
                      )}

                      {topThree[2] && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", order: 3, marginBottom: "-20px" }}>
                          <TopPlayerBadge rank={3} avatar={topThree[2].avatar_url} name={topThree[2].username} score={topThree[2].rating} time={formatElapsedTime(topThree[2].elapsed_time)} />
                        </Box>
                      )}
                    </Box>
                  )}

                  {rankFourPlus.length > 0 && (
                    <Box sx={{ marginBottom: "20px" }}>
                      {rankFourPlus.map((g, idx) => (
                        <PlayerRow
                          key={g.id || idx}
                          rank={idx + 4}
                          avatar={g.avatar_url}
                          name={g.username}
                          score={g.rating}
                          time={formatElapsedTime(g.elapsed_time)}
                          isCurrentUser={g.user_id && g.user_id.toString() === userId}
                        />
                      ))}
                    </Box>
                  )}

                  {userId && (
                    <>
                      {userResult && !isUserInTopFive && (
                        <>
                      <Box
  sx={{
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: { xs: "16px", sm: "22px" },
    my: 1.8,

    "&::before": {
      content: '""',
      flex: 1,
      height: "2px",
      maxWidth: "520px",
      background:
        "linear-gradient(to left, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.35) 55%, rgba(255,255,255,0.08) 100%)",
      boxShadow: "0 0 14px rgba(255,255,255,0.22)",
      borderRadius: "999px",
      clipPath: "polygon(0 0, 100% 25%, 100% 75%, 0 100%)",
    },

    "&::after": {
      content: '""',
      flex: 1,
      height: "2px",
      maxWidth: "520px",
      background:
        "linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.35) 55%, rgba(255,255,255,0.08) 100%)",
      boxShadow: "0 0 14px rgba(255,255,255,0.22)",
      borderRadius: "999px",
      clipPath: "polygon(0 25%, 100% 0, 100% 100%, 0 75%)",
    },
  }}
>
  <Typography
    sx={{
      color: "#ffffff",                 // ✅ bílá
      fontSize: { xs: "1.05rem", sm: "1.35rem" }, // ✅ větší
      fontFamily: "'Cinzel', serif",
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      fontWeight: 500,
      textShadow: "0 0 14px rgba(255,255,255,0.18)", // jemný glow
      whiteSpace: "nowrap",
    }}
  >
     TVOJE POZICE
  </Typography>
</Box>


                          <PlayerRow
                            rank={userRank}
                            avatar={userResult.avatar_url}
                            name={userResult.username}
                            score={userResult.rating}
                            time={formatElapsedTime(userResult.elapsed_time)}
                            isCurrentUser={true}
                          />
                        </>
                      )}

                      {!userResult && (
                        <>
                     <Box
  sx={{
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: { xs: "16px", sm: "22px" },
    my: 1.8,

    "&::before": {
      content: '""',
      flex: 1,
      height: "2px",
      maxWidth: "520px",
      background:
        "linear-gradient(to left, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.35) 55%, rgba(255,255,255,0.08) 100%)",
      boxShadow: "0 0 14px rgba(255,255,255,0.22)",
      borderRadius: "999px",
      clipPath: "polygon(0 0, 100% 25%, 100% 75%, 0 100%)",
    },

    "&::after": {
      content: '""',
      flex: 1,
      height: "2px",
      maxWidth: "520px",
      background:
        "linear-gradient(to right, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.35) 55%, rgba(255,255,255,0.08) 100%)",
      boxShadow: "0 0 14px rgba(255,255,255,0.22)",
      borderRadius: "999px",
      clipPath: "polygon(0 25%, 100% 0, 100% 100%, 0 75%)",
    },
  }}
>
  <Typography
    sx={{
      color: "#ffffff",                 // ✅ bílá
      fontSize: { xs: "1.05rem", sm: "1.35rem" }, // ✅ větší
      fontFamily: "'Cinzel', serif",
      letterSpacing: "0.14em",
      textTransform: "uppercase",
      fontWeight: 500,
      textShadow: "0 0 14px rgba(255,255,255,0.18)", // jemný glow
      whiteSpace: "nowrap",
    }}
  >
    TVOJE POZICE
  </Typography>
</Box>


                          <Box sx={{ display: "flex", alignItems: "center", paddingX: { xs: 1, sm: 1.5 }, paddingY: "8px", backgroundColor: "rgba(0, 0, 0, 0.2)", borderRadius: "8px", marginBottom: 1 }}>
                            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#9ea3a8", width: "16px", marginRight: "12px" }}>-</Typography>
                            <Box sx={{ position: "relative", width: 32, height: 32, marginRight: "10px", flexShrink: 0 }}>
                              <Avatar src={userAvatar} sx={{ width: "100%", height: "100%", border: "1.5px solid rgba(0, 0, 0, 0.3)" }} />
                            </Box>
                            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#f2ede4", flex: 1, minWidth: 0 }}>{username}</Typography>
                            <Typography sx={{ fontSize: "0.7rem", color: "rgba(242, 237, 228, 0.3)" }}>Zatím nehráno</Typography>
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </Box>
              );
            })()}
          </>
        )}
      </Box>

      {/* Spodní přepínač her */}
      {!!gameNames.length && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          
          height: { xs: 64, sm: 72 },                 // ✅ pevná výška
   paddingX: { xs: 12, sm: 16 },
   paddingBottom: "env(safe-area-inset-bottom)", // ✅ iPhone safe area
             background: "transparent",
    backdropFilter: "none",
          }}
        >
     <IconButton
  onClick={goPrev}
  disableRipple
  sx={{
    position: "absolute",
    left: 24,                 // 👈 tady nastavíš vzdálenost
    color: "rgba(242, 237, 228, 0.92)",
    backgroundColor: "transparent",
    border: "none",
    boxShadow: "none",
    borderRadius: 0,
    padding: "6px",
    "&:hover": { backgroundColor: "transparent" },
  }}
>
  <ChevronLeftIcon sx={{ fontSize: 28 }} />
</IconButton>


          <Typography
            sx={{
              color: "#F2EDE4",
              fontFamily: "'Cactus Classical Serif', serif",
              fontSize: { xs: "13px", sm: "14px" },
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              maxWidth: "70vw",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
            }}
          >
            {selectedGame}
          </Typography>

   <IconButton
  onClick={goNext}
  disableRipple
  sx={{
    position: "absolute",
    right: 24,               // 👈 zrcadlově
    color: "rgba(242, 237, 228, 0.92)",
    backgroundColor: "transparent",
    border: "none",
    boxShadow: "none",
    borderRadius: 0,
    padding: "6px",
    "&:hover": { backgroundColor: "transparent" },
  }}
>
  <ChevronRightIcon sx={{ fontSize: 28 }} />
</IconButton>

        </Box>
      )}
    </Box>
  );
};

export default Leaderboard;
